import { memo } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'

import { useAuth } from '@/lib/auth/AuthProvider'
import { useHealthCheck } from '@/lib/queries/useHealth'

import { NAV_ROUTE_GROUPS, navRouteGroupsForStage } from '@/constants/appRoutes'

import View, { type HealthStatus } from './Navbar.view'

const STAGE = import.meta.env.VITE_STAGE ?? 'unknown'
const STAGE_NAV_ROUTE_GROUPS = navRouteGroupsForStage(NAV_ROUTE_GROUPS, import.meta.env.VITE_STAGE)

export interface ControllerProps {
  className?: string
}

function getHealthStatus(isLoading: boolean, isError: boolean): HealthStatus {
  if (isLoading) return { text: 'Checking...', color: 'yellow', status: 'loading' }
  if (isError) return { text: 'API Offline', color: 'red', status: 'error' }
  return { text: 'API Online', color: 'green', status: 'healthy' }
}

export const Controller = memo(function NavbarController({ className }: ControllerProps) {
  const navigate = useNavigate()
  const activeRoute = useRouterState({ select: (state) => state.location.pathname })
  const { isError, isLoading } = useHealthCheck()
  const { user } = useAuth()
  const healthStatus = getHealthStatus(isLoading, isError)
  const routeGroups = user?.isAdmin
    ? STAGE_NAV_ROUTE_GROUPS
    : STAGE_NAV_ROUTE_GROUPS.map((group) => group.filter((route) => route.to !== '/settings')).filter(
        (group) => group.length
      )

  return (
    <View
      className={className}
      routeGroups={routeGroups}
      healthStatus={healthStatus}
      environment={STAGE.toUpperCase()}
      activeRoute={activeRoute}
      onRouteNavigate={(to) => void navigate({ to })}
    />
  )
})

Controller.displayName = 'Navbar_Controller'
